import React, { ReactNode } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { H3 } from '../../primitives';
import { colors, mixins } from '../../core/styles/theme/default';

type StatusColor = 'Level 1' | 'Level 2' | 'Level 3' | 'Level 4' | 'Level 5';

const gradients = {
  'Level 1': 'linear-gradient(90deg, #FFFF4B 0%, #86E18B 94.2%)',
  'Level 2': 'linear-gradient(90deg, #FFAF0A 0%, #FFFF4B 96.78%)',
  'Level 3': 'linear-gradient(90deg, #EF6706 0%, #FFAF0A 98.44%)',
  'Level 4': 'linear-gradient(90deg, #7E0D03 0%, #EF6706 96.89%)',
  'Level 5': 'linear-gradient(90deg, #320D03 0%, #7E0D03 97.33%)',
};

const StyledStatus = styled.article`
  background-color: ${colors.white};
  box-shadow: 0px 20px 44px rgba(0, 0, 0, 0.12),
    0px 10px 15px rgba(0, 0, 0, 0.06), 0px 4px 6px rgba(0, 0, 0, 0.04);
  height: 100%;
`;

const StyledStatusColor = styled.div(
  ({ status }: { status: StatusColor }) => css`
    height: ${rem(14)};
    width: 100%;
    background: ${gradients[status]};
  `,
);

const StyledContent = styled.div`
  padding: ${rem(32)};
  flex: 1;
  color: ${colors.grue800};
`;

const StyledTitle = styled(H3)`
  font-size: ${rem(24)};
  line-height: 138.6%;
  font-weight: 500;
  margin-bottom: ${rem(16)};
`;

const StyledDescription = styled.div`
  font-weight: 500;
  font-size: ${rem(20)};
  line-height: ${rem(31)};

  p {
    margin-bottom: ${rem(16)};
  }
`;

const StyledStatusLabel = styled.div`
  text-align: right;
  margin-right: ${rem(20)};
  margin-top: ${rem(6)};
  font-size: ${rem(10)};
  line-height: ${rem(20)};

  ${mixins.MQ('medium')} {
    font-size: ${rem(14)};
    line-height: ${rem(24)};
  }
`;

const Status = (props: StatusProps) => {
  const { title, description, status, statusLabel } = props.data;

  return (
    <>
      <StyledStatus className="status">
        <StyledStatusColor status={status} />
        <StyledStatusLabel>{statusLabel}</StyledStatusLabel>
        <StyledContent>
          {title ? <StyledTitle>{title}</StyledTitle> : null}
          <StyledDescription>{description}</StyledDescription>
        </StyledContent>
      </StyledStatus>
    </>
  );
};

export type StatusProps = {
  data: {
    title: string;
    description: ReactNode;
    status: StatusColor;
    statusLabel: string;
    variant?: string;
  };
};

export default Status;
